import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Tabs,
  Tab,
  useTheme
} from '@mui/material';
import {
  Article as ArticleIcon,
  PlayArrow as VideoIcon,
  TouchApp as InteractiveIcon,
  Quiz as QuizIcon,
  Description as DocIcon,
  Star as StarIcon,
  AccessTime as TimeIcon,
  School as SchoolIcon,
  TrendingUp as TrendingIcon,
  TouchApp as TouchAppIcon
} from '@mui/icons-material';
import { 
  LearningResource, 
  LearningStyleProfile, 
  ResourceRecommendation,
  MindMapNode,
  LearningPath
} from '../../types/learning';

interface ResourceRecommendationEngineProps {
  resources: LearningResource[];
  currentNode: MindMapNode | null;
  learningStyle: LearningStyleProfile;
  userProgress: Record<string, number>;
  learningPath: LearningPath;
  onResourceSelect: (resource: LearningResource) => void;
}

const ResourceRecommendationEngine: React.FC<ResourceRecommendationEngineProps> = ({
  resources,
  currentNode,
  learningStyle,
  userProgress,
  learningPath,
  onResourceSelect
}) => {
  const theme = useTheme();
  const [selectedTab, setSelectedTab] = useState(0);
  const [recommendations, setRecommendations] = useState<ResourceRecommendation[]>([]);

  // Calculate resource recommendations based on learning style and progress
  const calculateRecommendations = useMemo(() => {
    if (!currentNode) return [];

    const nodeResources = resources.filter(r => r.nodeId === currentNode.id);
    
    return nodeResources.map(resource => {
      const learningStyleMatch = calculateLearningStyleMatch(resource, learningStyle);
      const difficultyMatch = calculateDifficultyMatch(resource, userProgress[currentNode.id] || 0);
      const timeMatch = calculateTimeMatch(resource, learningPath.preferences.preferredSessionLength);
      const progressMatch = calculateProgressMatch(resource, userProgress);
      
      const overallScore = (
        learningStyleMatch * 0.4 +
        difficultyMatch * 0.3 +
        timeMatch * 0.2 +
        progressMatch * 0.1
      );

      const priority: 'high' | 'medium' | 'low' = overallScore > 0.8 ? 'high' : overallScore > 0.6 ? 'medium' : 'low';

      return {
        resourceId: resource.id,
        score: overallScore,
        reasoning: generateReasoning(resource, learningStyleMatch, difficultyMatch, timeMatch),
        learningStyleMatch: {
          visual: resource.learningStyleTags.visual || 0,
          auditory: resource.learningStyleTags.auditory || 0,
          reading: resource.learningStyleTags.reading || 0,
          kinesthetic: resource.learningStyleTags.kinesthetic || 0
        },
        difficultyMatch,
        timeMatch,
        priority
      };
    }).sort((a, b) => b.score - a.score);
  }, [resources, currentNode, learningStyle, userProgress, learningPath]);

  useEffect(() => {
    setRecommendations(calculateRecommendations);
  }, [calculateRecommendations]);

  const calculateLearningStyleMatch = (resource: LearningResource, style: LearningStyleProfile): number => {
    let match = 0;
    let totalWeight = 0;

    Object.entries(style).forEach(([key, value]) => {
      const resourceValue = resource.learningStyleTags[key as keyof LearningStyleProfile] || 0;
      match += value * resourceValue;
      totalWeight += value;
    });

    return totalWeight > 0 ? match / totalWeight : 0.5;
  };

  const calculateDifficultyMatch = (resource: LearningResource, currentMastery: number): number => {
    const idealDifficulty = Math.min(10, Math.max(1, currentMastery * 10 + 2));
    const difficultyDiff = Math.abs(resource.difficultyLevel - idealDifficulty);
    return Math.max(0, 1 - (difficultyDiff / 10));
  };

  const calculateTimeMatch = (resource: LearningResource, preferredTime: number): number => {
    const timeDiff = Math.abs(resource.estimatedTimeMinutes - preferredTime);
    const maxDiff = Math.max(preferredTime, 60);
    return Math.max(0, 1 - (timeDiff / maxDiff));
  };

  const calculateProgressMatch = (resource: LearningResource, progress: Record<string, number>): number => {
    // Check if prerequisites are met
    const unmetPrereqs = resource.prerequisites.filter(prereq => (progress[prereq] || 0) < 0.5);
    return unmetPrereqs.length === 0 ? 1 : 0.3;
  };

  const generateReasoning = (
    resource: LearningResource, 
    styleMatch: number, 
    difficultyMatch: number, 
    timeMatch: number
  ): string => {
    const reasons = [];
    
    if (styleMatch > 0.8) {
      reasons.push("Perfect match for your learning style");
    } else if (styleMatch > 0.6) {
      reasons.push("Good match for your learning preferences");
    }

    if (difficultyMatch > 0.8) {
      reasons.push("Appropriate difficulty level");
    }

    if (timeMatch > 0.8) {
      reasons.push("Fits your preferred session length");
    }

    if (resource.type === 'interactive') {
      reasons.push("Hands-on learning experience");
    } else if (resource.type === 'video') {
      reasons.push("Visual and auditory learning");
    } else if (resource.type === 'article') {
      reasons.push("Detailed reading material");
    }

    return reasons.join(" • ");
  };

  const getResourceIcon = (type: string) => {
    const icons = {
      article: <ArticleIcon />,
      video: <VideoIcon />,
      interactive: <InteractiveIcon />,
      quiz: <QuizIcon />,
      documentation: <DocIcon />,
      exercise: <TouchAppIcon />
    };
    return icons[type as keyof typeof icons] || <SchoolIcon />;
  };

  const getResourceColor = (type: string) => {
    const colors = {
      article: theme.palette.primary.main,
      video: theme.palette.secondary.main,
      interactive: theme.palette.success.main,
      quiz: theme.palette.warning.main,
      documentation: theme.palette.info.main,
      exercise: theme.palette.error.main
    };
    return colors[type as keyof typeof colors] || theme.palette.grey[500];
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      high: theme.palette.success.main,
      medium: theme.palette.warning.main,
      low: theme.palette.grey[500]
    };
    return colors[priority as keyof typeof colors] || theme.palette.grey[500];
  };

  const filteredRecommendations = useMemo(() => {
    switch (selectedTab) {
      case 0: // All
        return recommendations;
      case 1: // High Priority
        return recommendations.filter(r => r.priority === 'high');
      case 2: // Quick Learning
        return recommendations.filter(r => r.timeMatch > 0.8);
      case 3: // Interactive
        return recommendations.filter(r => {
          const resource = resources.find(res => res.id === r.resourceId);
          return resource?.type === 'interactive' || resource?.type === 'quiz';
        });
      default:
        return recommendations;
    }
  }, [recommendations, selectedTab, resources]);

  if (!currentNode) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Select a learning node to see recommended resources
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Recommended Resources
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Personalized recommendations for: <strong>{currentNode.title}</strong>
        </Typography>
      </Box>

      {/* Learning Style Summary */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Your Learning Style:</strong> {Object.entries(learningStyle)
            .filter(([_, value]) => value > 0.7)
            .map(([key, _]) => key.charAt(0).toUpperCase() + key.slice(1))
            .join(', ')} learner
        </Typography>
      </Alert>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={(_, value) => setSelectedTab(value)}>
          <Tab label="All Resources" />
          <Tab label="High Priority" />
          <Tab label="Quick Learning" />
          <Tab label="Interactive" />
        </Tabs>
      </Box>

      {/* Resource List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {filteredRecommendations.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No resources match your current filters
            </Typography>
          </Paper>
        ) : (
          filteredRecommendations.map((recommendation) => {
            const resource = resources.find(r => r.id === recommendation.resourceId);
            if (!resource) return null;

            return (
              <Card key={resource.id} sx={{ 
                border: recommendation.priority === 'high' ? `2px solid ${getPriorityColor(recommendation.priority)}` : '1px solid',
                borderColor: recommendation.priority === 'high' ? getPriorityColor(recommendation.priority) : 'divider'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Avatar sx={{ 
                      bgcolor: getResourceColor(resource.type),
                      width: 48,
                      height: 48
                    }}>
                      {getResourceIcon(resource.type)}
                    </Avatar>
                    
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6">
                          {resource.title}
                        </Typography>
                        {recommendation.priority === 'high' && (
                          <StarIcon sx={{ color: getPriorityColor(recommendation.priority) }} />
                        )}
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {resource.description}
                      </Typography>

                      {/* Match Indicators */}
                      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <Chip 
                          label={`${Math.round(recommendation.score * 100)}% Match`}
                          size="small"
                          color={recommendation.score > 0.8 ? 'success' : recommendation.score > 0.6 ? 'warning' : 'default'}
                        />
                        <Chip 
                          label={`${resource.estimatedTimeMinutes} min`}
                          size="small"
                          icon={<TimeIcon />}
                        />
                        <Chip 
                          label={`Level ${resource.difficultyLevel}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip 
                          label={resource.type}
                          size="small"
                          sx={{ bgcolor: getResourceColor(resource.type), color: 'white' }}
                        />
                      </Box>

                      {/* Learning Style Match */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          Learning Style Match:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {Object.entries(recommendation.learningStyleMatch).map(([style, value]) => (
                            <Box key={style} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                                {style}:
                              </Typography>
                              <LinearProgress 
                                variant="determinate" 
                                value={value * 100} 
                                sx={{ width: 40, height: 4 }}
                              />
                            </Box>
                          ))}
                        </Box>
                      </Box>

                      {/* Reasoning */}
                      <Typography variant="body2" color="text.secondary">
                        <strong>Why this resource:</strong> {recommendation.reasoning}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
                
                <CardActions>
                  <Button 
                    variant="contained" 
                    onClick={() => onResourceSelect(resource)}
                    startIcon={getResourceIcon(resource.type)}
                  >
                    Start Learning
                  </Button>
                  <Button variant="outlined">
                    Preview
                  </Button>
                </CardActions>
              </Card>
            );
          })
        )}
      </Box>

      {/* Learning Tips */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: 'background.default' }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingIcon />
          Learning Tips
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <SchoolIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Start with high-priority resources for maximum impact"
              secondary="These are specifically matched to your learning style and current progress"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <TimeIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Choose resources that fit your available time"
              secondary={`Your preferred session length: ${learningPath.preferences.preferredSessionLength} minutes`}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <TouchAppIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Mix different resource types for better retention"
              secondary="Combine reading, videos, and interactive exercises"
            />
          </ListItem>
        </List>
      </Paper>
    </Box>
  );
};

export default ResourceRecommendationEngine;
